import { skipToken, useMutation, useQuery } from "@tanstack/react-query";
import { baseApi } from "./baseApi";
import { engineApi } from "./engineApi";
import { API_URL } from "@/config";
import { LiveDeploymentResponse } from "./types";
import { CURRENT_BROKER_ID } from "@/config/logos";

interface EarningsApiItem {
  earnings_date: string;
  status: string;
  company_name: string;
  url: string;
  sigma: number;
  scaled_return: number;
}

export interface EarningsInfo {
  status: string;
  company_name: string;
  url: string;
  sigma: number;
  scaled_return: number;
}

type EarningsRecord = Record<string, EarningsInfo>;

interface EarningsApiItem {
  earnings_date: string;
  status: string;
  company_name: string;
  url: string;
  sigma: number;
  scaled_return: number;
}

export interface EarningsInfo {
  status: string;
  company_name: string;
  url: string;
  sigma: number;
  scaled_return: number;
}

type EarningsRecord = Record<string, EarningsInfo>;

export const usePostCreateStrategy = () => {
  return useMutation({
    mutationKey: ["create-strategy"],
    mutationFn: async ({
      symbol,
      timeframe,
      strategy_name,
      user_id,
      broker_id,
    }: {
      symbol?: string;
      timeframe?: string;
      strategy_name?: string;
      user_id: string;
      broker_id: string;
    }) => {
      if (!symbol || !timeframe || !strategy_name) return;
      const queryParams = new URLSearchParams({
        user_id,
        symbol,
        timeframe,
        strategy_name,
        broker_id,
      }).toString();

      const response = await baseApi.post(`/build_new_strategy?${queryParams}`);
      return response;
    },
  });
};

export const usePostCopyStrategy = () => {
  return useMutation({
    mutationKey: ["copy-strategy"],
    mutationFn: async ({
      userId,
      key,
      symbol,
      broker_id,
    }: {
      userId: string;
      key: string;
      symbol?: string;
      broker_id?: string;
    }) => {
      const response = await baseApi.post(
        `/copy_strategy?user_id=${userId}&strategy_key=${key}${
          symbol ? `&symbol=${symbol}` : ""
        }${CURRENT_BROKER_ID ? `&broker_id=${CURRENT_BROKER_ID}` : ""}`,
      );

      return response;
    },
  });
};

export const useGetLiveDeploymentStats = (user_id?: string) => {
  return useQuery({
    queryKey: ["get_live_deployments_aggregated_stats", user_id],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_live_deployments_aggregated_stats?user_id=${user_id}`,
          );
          return response;
        },
  });
};

export const useGetAllLiveDeployment = (
  user_id?: string,
  broker_id?: string,
  page?: number,
  search_key?: string,
) => {
  return useQuery({
    queryKey: [
      "get_all_live_deployments",
      user_id,
      broker_id,
      page,
      search_key,
    ],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const query = new URLSearchParams({
            user_id: user_id ?? "",
            broker_id: broker_id ?? "",
            page: String(page ?? 1),
            size: "6",
            ...(search_key ? { search_key } : {}),
          }).toString();

          const response = await baseApi.get<LiveDeploymentResponse>(
            `/get_all_live_deployments_v2?${query}`,
          );

          return response;
        },
  });
};

export const useGetMonitorDeploymentStats = (
  user_id?: string,
  broker_id?: string,
) => {
  return useQuery({
    queryKey: ["get_monitor_deployments_stats", user_id, broker_id],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_monitor_deployments_stats?user_id=${user_id}${
              broker_id ? `&broker_id=${broker_id}` : ""
            }`,
          );
          return response;
        },
  });
};

export const usePostGetMultiStrategy = (
  user_id?: string,
  broker_id?: string,
) => {
  return useQuery({
    queryKey: ["get_multi_asset_strategies", user_id],
    queryFn: async () => {
      const response = await baseApi.post(
        `/get_multi_asset_strategies?user_id=${user_id}`,
      );
      return response;
    },
    enabled: !!user_id,
  });
};

export const usePostUndeployMultiStrategy = () => {
  return useMutation({
    mutationKey: ["undeploy_live_multi_asset_strategy"],
    mutationFn: async ({
      userId,
      strategy_id,
    }: {
      userId: string;
      strategy_id: number;
    }) => {
      const response = await baseApi.post(
        `/undeploy_live_multi_asset_strategy?user_id=${userId}&strategy_id=${strategy_id}`,
      );
      return response;
    },
  });
};

export const usePostUndeployLiveStrategy = () => {
  return useMutation({
    mutationKey: ["undeploy_live_strategy"],
    mutationFn: async ({
      userId,
      strategy_id,
      dep_id,
    }: {
      userId: string;
      strategy_id: string;
      dep_id: string;
    }) => {
      const response = await baseApi.post(
        `/undeploy_live_strategy?user_id=${userId}&strategy_id=${strategy_id}&depl_id=${dep_id}`,
      );
      return response;
    },
  });
};

export const usePostChangeStrategyName = () => {
  return useMutation({
    mutationKey: ["change_strategy_name"],
    mutationFn: async ({
      userId,
      strategy_id,
      strategy_name,
    }: {
      userId: string;
      strategy_id: string;
      strategy_name: string;
    }) => {
      const response = await baseApi.post(
        `/change_strategy_name?user_id=${userId}&strategy_id=${strategy_id}&strategy_name=${strategy_name}`,
      );
      return response;
    },
  });
};

export const usePostDeployVirtualStrategy = () => {
  return useMutation({
    mutationKey: ["deploy_virtual_strategy"],
    mutationFn: async ({
      userId,
      strategy_id,
    }: {
      userId: string;
      strategy_id: string;
    }) => {
      const response = await baseApi.post(
        `/deploy_virtual_strategy?user_id=${userId}&strategy_id=${strategy_id}`,
      );
      return response;
    },
  });
};

export const usePostUndeployVirtualStrategy = () => {
  return useMutation({
    mutationKey: ["undeploy_virtual_strategy"],
    mutationFn: async ({
      user_id,
      deployment_id,
    }: {
      user_id: string;
      deployment_id: string;
    }) => {
      const response = await baseApi.post(`/undeploy_virtual_strategy`, {
        user_id,
        deployment_id,
      });
      return response;
    },
  });
};

export const useGetAllLiveDeploymentOrderStatus = () => {
  return useMutation({
    mutationKey: ["get_live_deployments_order_status"],
    mutationFn: async ({
      user_id,
      data,
    }: {
      user_id?: string;
      data?: any[];
    }) => {
      const url = new URL(
        "/get_live_deployments_order_status",
        baseApi.defaults.baseURL,
      );
      if (user_id) url.searchParams.append("user_id", user_id);

      if (data?.length) {
        const strategyIds = data
          .map((inst) => inst.deployment_meta.strategy.id)
          .join(",");
        url.searchParams.append("strategy_ids", strategyIds);
      } else {
        return;
      }

      const response = await baseApi.get(url.toString());
      return response;
    },
  });
};

export const useGetAllLiveDeploymentOrders = () => {
  return useMutation({
    mutationKey: ["get_live_deployments_order_status"],
    mutationFn: async ({
      user_id,
      strategyIds,
    }: {
      user_id: string;
      strategyIds: string;
    }) => {
      const url = new URL(
        "/get_live_deployments_order_status",
        baseApi.defaults.baseURL,
      );
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("strategy_ids", strategyIds);
      const response = await baseApi.get(url.toString());
      return response;
    },
  });
};

export const useGetLogos = () => {
  return useMutation({
    mutationKey: ["get_logos"],
    mutationFn: async ({ symbols }: { symbols?: any[] }) => {
      const url = new URL("/get_logos", baseApi.defaults.baseURL);
      if (symbols) url.searchParams.append("symbols", symbols?.toString());
      else return;
      const response = await baseApi.get(url.toString());
      return response;
    },
  });
};

export const useGetStrategyTrainerTPSL = () => {
  return useMutation({
    mutationKey: ["strategy_trainer_try_tpsl_v2"],
    mutationFn: async ({
      user_id,
      strategy_id,
      tp,
      sl,
    }: {
      user_id: string;
      strategy_id: string;
      tp: string;
      sl: string;
    }) => {
      const response = await baseApi.get(
        `/strategy_trainer_try_tpsl_v2?user_id=${user_id}&strategy_id=${strategy_id}&tp=${tp}&sl=${sl}`,
      );
      return response;
    },
  });
};

export const useGetStrategyTrainerAction = () => {
  return useMutation({
    mutationKey: ["strategy_trainer_try_action_v2"],
    mutationFn: async ({
      user_id,
      strategy_id,
      action,
    }: {
      user_id: string;
      strategy_id: string;
      action: string;
    }) => {
      const response = await baseApi.get(
        `/strategy_trainer_try_action_v2?user_id=${user_id}&strategy_id=${strategy_id}&action=${action}`,
      );
      return response;
    },
  });
};

export const useGetPL = (
  user_id?: string,
  type?: string,
  deployment_id?: string,
) => {
  return useQuery({
    queryKey: ["get_p_l_live_deployed_strategies", user_id, type],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_p_l_${type}_deployed_strategies?user_id=${user_id}${
          deployment_id ? `&deployment_id=${deployment_id}` : ""
        }`,
      );
      return response;
    },
    enabled: !!user_id,
    refetchOnMount: false, // Always fetch when component mounts
    refetchInterval: 5000, //Polling data every 3 sec to get live data
  });
};

export const useGetExpiredStrategies = () => {
  return useMutation({
    mutationKey: ["get_live_expired_strategies_v2"],
    mutationFn: async ({
      user_id,
      search_key,
      page,
      size,
    }: {
      user_id?: string;
      search_key?: string;
      page?: number;
      size?: number;
    }) => {
      const response = await baseApi.get(
        `/get_live_expired_strategies_v2?user_id=${user_id}&search_key=${search_key}&page=${page}&size=${size}`,
      );
      return response;
    },
  });
};

export const useGetLiveStrategiesVolumes = () => {
  return useMutation({
    mutationKey: ["get_live_deployment_strategies_volume"],
    mutationFn: async ({
      user_id,
      strategyIds,
    }: {
      user_id: string;
      strategyIds: string;
    }) => {
      const url = new URL(
        "/get_live_deployment_strategies_volume",
        baseApi.defaults.baseURL,
      );
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("strategy_ids", strategyIds);

      const response = await baseApi.get(url.toString());
      return response;
    },
  });
};

export const useGetExpiredPLs = () => {
  return useMutation({
    mutationKey: ["get_virtual_deps_pnl"],
    mutationFn: async ({
      user_id,
      type,
      deploymentIds,
    }: {
      user_id?: string;
      type?: string;
      deploymentIds?: string;
    }) => {
      const url = new URL(`/get_${type}_deps_pnl`, baseApi.defaults.baseURL);

      if (deploymentIds) {
        url.searchParams.append("deployment_ids", deploymentIds);
      }
      if (user_id) {
        url.searchParams.append("user_id", user_id);
      }

      const response = await baseApi.get(url.toString());
      return response.data;
    },
  });
};

export const useGetStrategiesByUser = ({
  user_id,
  page,
  search_key,
}: {
  user_id?: string;
  page?: string;
  search_key?: string;
}) => {
  return useQuery({
    queryKey: ["get_strategies_by_user", user_id, page, search_key],
    queryFn: async () => {
      const params = new URLSearchParams({
        user_id: user_id || "",
        page: page || "1",
        is_copied: "0",
        size: "6",
      });

      if (search_key) {
        params.append("search_key", search_key);
      }

      const response = await baseApi.get(
        `/get_strategies_by_user?${params.toString()}`,
      );
      return response;
    },
    enabled: !!user_id,
  });
};

export const useGetStrategyPerformanceStats = (user_id?: string) => {
  return useQuery({
    queryKey: ["get_strategy_performance_stats", user_id],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_strategy_performance_stats?user_id=${user_id}`,
      );
      return response;
    },
    enabled: !!user_id,
  });
};

export const usePostUnDeployNotificationDeployment = () => {
  return useMutation({
    mutationKey: ["undeploy_notification_deployment"],
    mutationFn: async ({
      user_id,
      deployment_id,
    }: {
      user_id?: string;
      deployment_id?: string;
    }) => {
      const response = await baseApi.post(
        `/undeploy_notification_deployment?user_id=${user_id}&deployment_id=${deployment_id}`,
      );
      return response.data;
    },
  });
};

export const usePostDeleteMultipleStrategies = () => {
  return useMutation({
    mutationKey: ["delete_multiple_strategies"],
    mutationFn: async ({
      user_id,
      strategy_ids,
    }: {
      user_id?: string;
      strategy_ids?: string;
    }) => {
      const response = await baseApi.post(
        `/delete_multiple_strategies?user_id=${user_id}&strategy_ids=${strategy_ids}`,
      );
      return response.data;
    },
  });
};

export const usePostPublishStrategies = () => {
  return useMutation({
    mutationKey: ["profile_publish_strategy"],
    mutationFn: async ({
      user_id,
      strategy_id,
      publish_state,
    }: {
      user_id?: string;
      strategy_id?: string;
      publish_state?: string;
    }) => {
      const response = await baseApi.post(
        `/profile_publish_strategy?user_id=${user_id}&strategy_id=${strategy_id}&publish_state=${publish_state}`,
      );
      return response.data;
    },
  });
};

export const useGetStrategyDetails = (
  user_id?: string,
  strategy_id?: string,
  deployment_id?: string,
) => {
  return useQuery({
    queryKey: ["get_strategy_details", user_id, strategy_id],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_strategy_details?user_id=${user_id}&strategy_id=${strategy_id}${
          deployment_id ? "&depl_id=" + deployment_id : ""
        }`,
      );
      return response;
    },
    enabled: !!user_id && !!strategy_id,
    staleTime: 1,
    refetchOnWindowFocus: false,
  });
};

export const useGetMultiStrategyDetails = (
  user_id?: string,
  strategy_id?: string,
  deployment_id?: string,
) => {
  return useQuery({
    queryKey: ["get_strategy_details", user_id, strategy_id],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_strategy_details_for_multiasset?user_id=${user_id}&strategy_id=${strategy_id}${
          deployment_id ? "&depl_id=" + deployment_id : ""
        }`,
      );
      const type = response.data?.deployment?.strategy_meta?.dep_flag;
      if (
        type === "multi" &&
        response.data?.deployment?.strategy_meta?.deployed_symbols
      ) {
        response.data.strategy_details.symbol =
          response.data.deployment.strategy_meta.deployed_symbols;
      }
      return response;
    },
    enabled: !!user_id && !!strategy_id,
    staleTime: 1,
    refetchOnWindowFocus: false,
  });
};

export const useGetStrategyDetailsMutation = () => {
  return useMutation({
    mutationKey: ["get_strategy_details"],
    mutationFn: async ({
      user_id,
      strategy_id,
    }: {
      user_id: string;
      strategy_id: string;
    }) => {
      const response = await baseApi.get(
        `/get_strategy_details?user_id=${user_id}&strategy_id=${strategy_id}`,
      );
      return response;
    },
  });
};

export const usePostGetBacktestData = () => {
  return useMutation({
    mutationKey: ["backtest_data_task"],
    mutationFn: async ({
      strategy_keygen,
      user_id,
      broker_id,
    }: {
      strategy_keygen: string;
      user_id?: string;
      broker_id?: string;
    }) => {
      const formData = new URLSearchParams();
      formData.append("strategy_keygen", strategy_keygen);

      const queryParams = new URLSearchParams();
      if (user_id) queryParams.append("user_id", user_id);
      if (broker_id) queryParams.append("broker_id", broker_id);

      const url = `/backtest_data_task${
        queryParams.toString() ? `?${queryParams.toString()}` : ""
      }`;

      const response = await engineApi.post(url, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      return response;
    },
    retry: 10,
    retryDelay: 1000,
  });
};

export const usePostBacktestTaskResult = () => {
  return useMutation({
    mutationKey: ["backtest_task_result"],
    mutationFn: async ({ task_id }: { task_id: string }) => {
      const response = await engineApi.post(
        `/backtest_task_result?task_id=${task_id}`,
      );
      return response;
    },
  });
};

export const usePostBacktestQueue = () => {
  return useMutation({
    mutationKey: ["backtest_queue"],
    mutationFn: async ({ formData }: { formData: any }) => {
      const response = await engineApi.post(`/backtest_queue`, formData, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });
      return response;
    },
  });
};

export const usePostBacktestStatus = () => {
  return useMutation({
    mutationKey: ["backtest_status"],
    mutationFn: async ({ task_id }: { task_id: string }) => {
      const response = await engineApi.get(
        `/backtest_status?task_id=${task_id}`,
      );
      return response;
    },
    retry: 10,
    retryDelay: 1000,
  });
};

export const useGetBacktestStatus = () => {
  return useMutation({
    mutationKey: ["get_backtest_status"],
    mutationFn: async ({ strategy_keygen }: { strategy_keygen: string }) => {
      const formData = new URLSearchParams();
      formData.append("strategy_keygen", strategy_keygen);
      const response = await engineApi.get(
        `/get_backtest_status?strategy_keygen=${strategy_keygen}`,
      );

      return response;
    },
    retry: 10,
    retryDelay: 1000,
  });
};

// get_all_report_versions
export const useGetAllReportVersions = () => {
  return useMutation({
    mutationKey: ["get_all_report_versions"],
    mutationFn: async ({
      user_id,
      strategy_id,
    }: {
      user_id: string;
      strategy_id: string;
    }) => {
      const response = await baseApi.get(
        `/get_all_report_versions?user_id=${user_id}&strategy_id=${strategy_id}`,
      );
      return response;
    },
  });
};

export const usePostBacktestTimeframeQueue = () => {
  return useMutation({
    mutationKey: ["backtest_timeframe_queue"],
    mutationFn: async ({
      user_id,
      strategy_id,
      formData,
    }: {
      user_id: string;
      strategy_id: string;
      formData: any;
    }) => {
      const response = await engineApi.post(
        `/backtest_timeframe_queue?user_id=${user_id}&strategy_id=${strategy_id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );
      return response;
    },
  });
};

export const usePostBacktestTimeframeQueueV2 = () => {
  return useMutation({
    mutationKey: ["backtest_timeframe_queuev2"],
    mutationFn: async ({
      user_id,
      strategy_id,
      broker_id,
      formData,
      start_date,
      end_date,
    }: {
      user_id: string;
      strategy_id: string;
      broker_id?: string;
      formData: any;
      start_date?: string;
      end_date?: string;
    }) => {
      // Build query string safely
      const query = new URLSearchParams({ user_id, strategy_id });
      if (broker_id) query.append("broker_id", broker_id);
      if (start_date) query.append("start_date", start_date);
      if (end_date) query.append("end_date", end_date);

      const response = await engineApi.post(
        `/backtest_timeframe_queue_v2?${query.toString()}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );

      return response;
    },
  });
};

export const usePostMediumBacktest = () => {
  return useMutation({
    mutationKey: ["medium_backtest"],
    mutationFn: async ({
      formData,
      user_id,
      broker_id,
      start_date,
      end_date,
    }: {
      formData: any;
      user_id: string;
      broker_id?: string;
      start_date?: string;
      end_date?: string;
    }) => {
      // Construct the query string conditionally
      const query = new URLSearchParams({ user_id });
      if (broker_id) query.append("broker_id", broker_id);
      if (start_date) query.append("start_date", start_date);
      if (end_date) query.append("end_date", end_date);

      const response = await engineApi.post(
        `/medium_backtest?${query.toString()}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );

      return response;
    },
  });
};

export const usePostChangeTimeFrame = () => {
  return useMutation({
    mutationKey: ["change_strategy_timeframe"],
    mutationFn: async ({
      user_id,
      strategy_id,
      timeframe,
    }: {
      user_id: string;
      strategy_id: string;
      timeframe: string;
    }) => {
      const response = await baseApi.post(
        `/change_strategy_timeframe?user_id=${user_id}&strategy_id=${strategy_id}&timeframe=${timeframe}`,
      );
      return response;
    },
  });
};
export const useGetMediumBacktestStatus = () => {
  return useMutation({
    mutationKey: ["get_backtest_status"],
    mutationFn: async ({ task_id }: { task_id: string }) => {
      const response = await engineApi.get(
        `/get_backtest_status?task_id=${task_id}`,
      );
      return response;
    },
    retry: 10,
  });
};

export const useGetBacktestTimeframeStatus = () => {
  return useMutation({
    mutationKey: ["backtest_timeframe_status"],
    mutationFn: async ({ task_id }: { task_id: string }) => {
      const response = await engineApi.get(
        `/backtest_timeframe_status?task_id=${task_id}`,
      );
      return response;
    },
    retry: 3,
  });
};

export const usePostChangeStrategy = () => {
  return useMutation({
    mutationKey: ["backtest_timeframe_queue"],
    mutationFn: async ({
      user_id,
      strategy_id,
      formData,
    }: {
      user_id: string;
      strategy_id: string;
      formData: any;
    }) => {
      const response = await baseApi.post(
        `/change_strategy_via_trainer?user_id=${user_id}&strategy_id=${strategy_id}`,
        formData,
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        },
      );
      return response;
    },
  });
};

export const usePostCommonIndicators = () => {
  return useMutation({
    mutationKey: ["indicator_value"],
    mutationFn: async ({
      indicator,
      formData,
    }: {
      indicator: string;
      formData: FormData;
    }) => {
      const response = await engineApi.post(
        `/indicator_value?indicator=${indicator}`,
        formData,
      );
      return response?.data;
    },
  });
};

export const usePostEarningsIndicator = () => {
  return useMutation<
    Record<
      string,
      {
        earnings_data: EarningsRecord;
        realtime_data: {
          sigma: number;
          scaled_return: number;
        };
      }
    >,
    Error,
    {
      ticker: string;
      number_of_earnings?: number;
      holding_period_days?: number;
    }
  >({
    mutationKey: ["post_earnings_indicator"],
    mutationFn: async ({ ticker, number_of_earnings, holding_period_days }) => {
      const response = await engineApi.get<
        Record<
          string,
          {
            earning_data: EarningsApiItem[];
            realtime_data: EarningsApiItem;
          }
        >
      >(
        `/get_past_earnings_dates?ticker=${ticker}` +
          (number_of_earnings
            ? `&number_of_earnings=${number_of_earnings}`
            : "") +
          (holding_period_days
            ? `&holding_period_days=${holding_period_days}`
            : ""),
      );

      return Object.entries(response.data).reduce(
        (acc, [symbol, payload]) => {
          const earningsData = payload.earning_data.reduce<EarningsRecord>(
            (innerAcc, item) => {
              innerAcc[item.earnings_date] = {
                status: item.status,
                company_name: item.company_name,
                url: item.url,
                sigma: item.sigma,
                scaled_return: item.scaled_return,
              };
              return innerAcc;
            },
            {},
          );

          acc[symbol] = {
            earnings_data: earningsData,
            realtime_data: {
              sigma: payload.realtime_data.sigma,
              scaled_return: payload.realtime_data.scaled_return,
            },
          };

          return acc;
        },
        {} as Record<
          string,
          {
            earnings_data: EarningsRecord;
            realtime_data: {
              sigma: number;
              scaled_return: number;
            };
          }
        >,
      );
    },
  });
};

export const useGetCategories = () => {
  return useQuery({
    queryKey: ["get_strategy_categories"],
    queryFn: async () => {
      const response = await baseApi.get(`/get_strategy_categories`);
      return response;
    },
  });
};

export const useGetCheckForex = (symbol?: string) => {
  return useQuery({
    queryKey: ["check_symbol_forex", symbol],
    queryFn: async () => {
      try {
        const response = await baseApi.get(
          `/check_symbol_forex?symbol=${symbol}`,
        );
        return response;
      } catch {
        console.error("failed get recent strategies");
      }
    },
    enabled: !!symbol,
  });
};

export const useGetCheckAllBrokers = (user_id?: string) => {
  return useQuery({
    queryKey: ["get_all_user_brokers", user_id],
    queryFn: async () => {
      try {
        const response = await baseApi.get(
          `/get_all_user_brokers?user_id=${user_id}`,
        );
        return response;
      } catch {
        console.error("failed get recent strategies");
      }
    },
    enabled: !!user_id,
  });
};

export const usePostDeployLiveStrategy = () => {
  return useMutation({
    mutationKey: ["deploy_live_strategy"],
    mutationFn: async ({
      user_id,
      strategy_id,
      size,
      leverage,
      broker_id,
    }: {
      user_id: string;
      strategy_id: string;
      size: string;
      leverage: string;
      broker_id: string;
    }) => {
      const url = new URL("/deploy_live_strategy", API_URL);
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("strategy_id", strategy_id);
      url.searchParams.append("size", size);
      url.searchParams.append("leverage", leverage);
      url.searchParams.append("broker_id", broker_id);

      const response = await baseApi.post(url.toString());
      return response.data;
    },
  });
};

export const usePostDeployMultiStrategy = () => {
  return useMutation({
    mutationKey: ["deploy_live_multi_asset_strategy"],
    mutationFn: async ({
      user_id,
      strategy_id,
      size,
      leverage,
      broker_id,
      symbols,
      timeframe,
    }: {
      user_id: string;
      strategy_id: string;
      size: string;
      symbols: string;
      broker_id: string;
      timeframe: string;
      leverage?: string;
    }) => {
      const url = new URL("/deploy_live_multi_asset_strategy", API_URL);
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("strategy_id", strategy_id);
      url.searchParams.append("size", size);
      leverage && url.searchParams.append("leverage", leverage);
      url.searchParams.append("broker_id", broker_id);
      url.searchParams.append("symbols", symbols);
      url.searchParams.append("timeframe", timeframe);
      const response = await baseApi.post(url.toString());
      return response.data;
    },
  });
};

export const usePostSetStrategySize = () => {
  return useMutation({
    mutationKey: ["set_strategy_size"],
    mutationFn: async ({
      user_id,
      strategy_id,
      size,
    }: {
      user_id: string;
      strategy_id: string;
      size: string;
    }) => {
      const url = new URL("/set_strategy_size", API_URL);
      url.searchParams.append("user_id", user_id);
      url.searchParams.append("strategy_id", strategy_id);
      url.searchParams.append("size", size);
      const response = await baseApi.post(url.toString());
      return response.data;
    },
  });
};

export const useGetDeployLiveStats = () => {
  return useMutation({
    mutationKey: ["get_deploy_live_stats"],
    mutationFn: async ({
      user_id,
      strategy_id,
    }: {
      user_id?: string;
      strategy_id?: string;
    }) => {
      const response = await baseApi.get(
        `/get_deploy_live_stats?user_id=${user_id}&strategy_id=${strategy_id}`,
      );
      return response.data;
    },
  });
};

export const useGetStrategyExplaination = (strategy_id?: string) => {
  return useQuery({
    queryKey: ["get_strategy_explanation", strategy_id],
    queryFn: async () => {
      const response = await engineApi.get(
        `/get_strategy_explanation?strategy_id=${strategy_id}`,
      );
      return response;
    },
    enabled: !!strategy_id,
  });
};

export const useGetShowcaseStrategy = (userId?: string, broker_id?: string) => {
  return useQuery({
    queryKey: ["get_showcase_strategies", userId],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_showcase_strategies?user_id=${userId}&broker_id=${broker_id}`,
      );
      return response;
    },
    enabled: !!userId,
  });
};

export const useGetStrategyAddons = (
  user_id?: string,
  strategy_id?: string,
  deployment_id?: string,
) => {
  return useQuery({
    queryKey: ["get_strategy_add_ons", user_id, strategy_id],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_strategy_add_ons?user_id=${user_id}&strategy_id=${strategy_id}${
          deployment_id ? "&depl_id=" + deployment_id : ""
        }`,
      );
      return response;
    },
    enabled: !!user_id && !!strategy_id,
    staleTime: 1,
    refetchOnWindowFocus: false,
  });
};

export const useGetStrategyPerformance = (
  strategy_id?: string,
  deployment_id?: string,
  enabled?: boolean,
) => {
  return useQuery({
    queryKey: [
      "broker_get_user_strategy_performance",
      strategy_id,
      deployment_id,
    ],
    queryFn: async () => {
      const response = await baseApi.get(
        `/broker_get_user_strategy_performance?strategy_id=${strategy_id}&deployment_id=${deployment_id}`,
      );
      return response;
    },
    enabled: enabled,
    staleTime: 1,
    refetchOnWindowFocus: false,
  });
};

export const useGetPostTradeAnalysis = (
  user_id?: string,
  deployment_id?: string,
) => {
  return useQuery({
    queryKey: ["get_post_trade_analysis", user_id, deployment_id],
    queryFn: async () => {
      const response = await baseApi.get(
        `/get_post_trade_analysis?user_id=${user_id}&deployment_id=${deployment_id}`,
      );
      return response;
    },
    enabled: !!deployment_id,
    staleTime: 1,
    refetchInterval: 45000,
    refetchOnWindowFocus: false,
  });
};

export const useGetDeploymentHint = (
  user_id?: string,
  deployment_id?: string,
) => {
  return useQuery({
    queryKey: ["get_deployment_hint_v2", user_id, deployment_id],
    queryFn: !user_id
      ? skipToken
      : async () => {
          const response = await baseApi.get(
            `/get_deployment_hint_v2?user_id=${user_id}${
              deployment_id ? `&deployment_id=${deployment_id}` : ""
            }`,
          );
          return response;
        },
  });
};

export const useGetDeploymentLogs = (deployment_id: string) => {
  return useQuery({
    queryKey: ["get_deployment_log", deployment_id],
    queryFn: async () => {
      const response = await engineApi.post(
        `/get_deployment_log?deployment_id=${deployment_id}`,
      );
      return response;
    },
    refetchOnMount: false, // Always fetch when component mounts
    refetchInterval: 30000, //Polling data every 3 sec to get live data
  });
};
